import { Highlighter, MessageSquare } from 'lucide-react';
import {
  annotationEntries,
  type RefuseAnnotation,
} from '../../services/signature/refuseAnnotations';

interface Props {
  annotations: RefuseAnnotation[];
  refuseMode: boolean;
  refuseTool: 'highlight' | 'note';
  onTool: (tool: 'highlight' | 'note') => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onChangeText: (id: string, text: string) => void;
  showMarks: boolean;
  onShowMarks: (show: boolean) => void;
}

export function RefuseMarksPanel({
  annotations,
  refuseMode,
  refuseTool,
  onTool,
  selectedId,
  onSelect,
  onChangeText,
  showMarks,
  onShowMarks,
}: Props) {
  const entries = annotationEntries(annotations);
  const selected = annotations.find((a) => a.id === selectedId) ?? null;

  function scrollTo(page: number) {
    document.querySelector(`[data-page="${page}"]`)?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }

  return (
    <div className="space-y-3">
      {annotations.length > 0 && (
        <label className="flex items-center gap-2 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-800 cursor-pointer">
          <input
            type="checkbox"
            className="h-4 w-4 accent-cime-primary"
            checked={showMarks}
            onChange={(e) => onShowMarks(e.target.checked)}
          />
          Afficher les annotations
        </label>
      )}

      {refuseMode && (
        <div className="rounded-md border border-amber-200 bg-amber-50/80 px-3 py-2.5 space-y-2">
          <p className="text-xs font-medium text-amber-950">Annoter le refus</p>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => onTool('highlight')}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-xs ${
                refuseTool === 'highlight' ? 'border-amber-700 bg-white text-amber-950' : 'border-neutral-200 bg-white/50'
              }`}
            >
              <Highlighter className="w-3 h-3" /> Surligner
            </button>
            <button
              type="button"
              onClick={() => onTool('note')}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-xs ${
                refuseTool === 'note' ? 'border-amber-700 bg-white text-amber-950' : 'border-neutral-200 bg-white/50'
              }`}
            >
              <MessageSquare className="w-3 h-3" /> Pastille
            </button>
          </div>
          {refuseTool === 'highlight' && (
            <p className="text-[11px] text-amber-900">
              Glissez sur le passage. Un champ s’ouvre pour la remarque.
            </p>
          )}
          {refuseTool === 'note' && (
            <p className="text-[11px] text-amber-900">
              Cliquez le PDF pour poser la pastille, puis écrivez ici.
            </p>
          )}
          {selected && (
            <textarea
              value={selected.text ?? ''}
              onChange={(e) => onChangeText(selected.id, e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              rows={3}
              placeholder={
                selected.kind === 'highlight'
                  ? 'Pourquoi ce passage pose problème…'
                  : 'Contenu de la pastille…'
              }
              className="w-full border border-amber-300 rounded-md px-2 py-1.5 text-sm text-neutral-800 bg-white"
            />
          )}
        </div>
      )}

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-2">
          Remarques sur le PDF
          {entries.length > 0 && (
            <span className="ml-1 font-normal text-neutral-400">({entries.length})</span>
          )}
        </p>
        {entries.length === 0 ? (
          <p className="text-xs text-cime-text-muted">
            {refuseMode ? 'Aucun passage marqué pour l’instant.' : 'Aucune remarque.'}
          </p>
        ) : (
          <ul className="space-y-2">
            {entries.map((e) => {
              const active = e.id === selectedId;
              return (
                <li key={e.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onSelect(e.id);
                      scrollTo(e.page);
                    }}
                    className={`w-full text-left rounded-md border px-2.5 py-2 ${
                      active
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-neutral-200 bg-white hover:border-neutral-300'
                    }`}
                  >
                    <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-neutral-500">
                      {e.kind === 'highlight' ? (
                        <Highlighter className="w-3 h-3 text-amber-600" />
                      ) : (
                        <MessageSquare className="w-3 h-3 text-amber-600" />
                      )}
                      p. {e.page}
                      <span className="normal-case tracking-normal">
                        {e.kind === 'highlight' ? 'Surlignage' : 'Pastille'}
                      </span>
                    </span>
                    {e.text ? (
                      <span className="mt-1 block text-sm text-neutral-800 whitespace-pre-wrap">{e.text}</span>
                    ) : (
                      <span className="mt-1 block text-xs italic text-neutral-400">
                        {refuseMode ? 'Cliquez pour écrire la remarque' : 'Sans commentaire'}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
